import { useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { EVENTS, identifyUser, resetAnalytics, setAnalyticsWorkspace, track } from '@/lib/analytics';
import { setMonitoredUser } from '@/lib/errorMonitoring';

const SIGNUP_WINDOW_MS = 10 * 60 * 1000;

/**
 * Keeps analytics identity in step with the app's auth + workspace state, so
 * neither context needs to know analytics exists. Renders nothing.
 *
 * `signed_up` is inferred from the profile's created_at rather than fired from
 * the register form: that way Google sign-ups (which never touch the form) are
 * counted too. A per-user localStorage flag stops reloads from double counting.
 */
export default function AnalyticsBridge() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const lastUserId = useRef(null);

  useEffect(() => {
    const id = user?.id || null;
    if (id === lastUserId.current) return;
    setMonitoredUser(user);
    if (id) {
      identifyUser(user);
      const createdAt = user.created_at ? Date.parse(user.created_at) : NaN;
      const flag = `ff_signup_tracked_${id}`;
      try {
        if (!Number.isNaN(createdAt) && Date.now() - createdAt < SIGNUP_WINDOW_MS && !localStorage.getItem(flag)) {
          localStorage.setItem(flag, '1');
          track(EVENTS.SIGNED_UP, { method: user.app_metadata?.provider || 'email' });
        }
      } catch (e) { /* storage blocked — skip rather than risk double counting */ }
    } else if (lastUserId.current) {
      resetAnalytics();
    }
    lastUserId.current = id;
  }, [user]);

  useEffect(() => {
    if (user?.id && currentWorkspace?.id) setAnalyticsWorkspace(currentWorkspace);
  }, [user?.id, currentWorkspace?.id, currentWorkspace?.plan_id, currentWorkspace?.billing_status]);

  return null;
}
